import moment from "moment";
import { useEffect, useRef, useState } from "react";
import {
  getCaseNumberFormat,
  getEvidenceNumberFormat,
  getStorageNumberFormat,
  logActivity,
  updateCaseNumberFormat,
  updateEvidenceNumberFormat,
  updateStorageNumberFormat,
} from "../../api";
import { useSnackbar } from "notistack";

import { SelectBox, TextInput } from "@monolith-forensics/monolith-ui";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "@mui/material";
import { Divider } from "@monolith-forensics/monolith-ui";
import Loader from "../../components/Loader";
import styled from "styled-components";
import SettingSwitchGroup from "@/components/TenantSettings/SettingSwitchGroup";
import { MONOLITH_PERMISSIONS } from "@/constants";
import TenantSettingsAPI from "@/api/tenant-settings";
import { SettingEntity } from "@/components/TenantSettings/SettingSwitchGroup";
import { useQueryClient } from "@tanstack/react-query";
import { useFeatures } from "../../contexts/FeaturesContext";
import {
  AssetNumberFormatSection,
  getAssetNumberFormat,
  updateAssetNumberFormat,
} from "@/api/lab-assets";

interface ItemNumberFormat {
  format_id?: number;
  format_mod?: string | number;
  format_section: string;
  initial_text?: string;
  iterator?: number | string;
  restart?: number;
  starting_value?: number;
}

const buttonStyle = {
  margin: "10px 0px",
  marginRight: "10px",
};

const clampNumber = (raw: string, min: number, max?: number): number => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return min;
  if (max !== undefined && n > max) return max;
  if (n < min) return min;
  return n;
};

const CaseNumberFormat = () => {
  const { currentUser } = useAuth();
  const [example, setExample] = useState("");
  const [currentFormat, setCurrentFormat] = useState<ItemNumberFormat[] | []>(
    []
  );
  const [fieldError, setFieldError] = useState(false);
  const savedFormat = useRef<ItemNumberFormat[] | null>(null);
  const isNumberUsed = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getCaseNumberFormat().then((result) => {
      isNumberUsed.current = result.find((r: ItemNumberFormat) => {
        return r.format_section === "Number";
      });
      savedFormat.current = result;
      setExample(getExample(result));
      setCurrentFormat(result);
    });
  }, []);

  const addText = () => {
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Text",
      },
    ]);
  };

  const addNumber = () => {
    if (!isNumberUsed.current) {
      isNumberUsed.current = true;
      setFieldError(false);
      setExample(
        getExample([
          ...currentFormat,
          {
            format_section: "Number",
            format_mod: 1,
            iterator: 1,
          },
        ])
      );
      setCurrentFormat([
        ...currentFormat,
        {
          format_section: "Number",
          format_mod: 1,
          iterator: 1,
        },
      ]);
    }
  };

  const addYear = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Year",
          format_mod: "YY",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Year",
        format_mod: "YY",
      },
    ]);
  };

  const addMonth = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Month",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Month",
      },
    ]);
  };

  const addDay = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Day",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Day",
      },
    ]);
  };

  const getExample = (formatData: ItemNumberFormat[] | null) => {
    let example = "";

    if (formatData) {
      formatData.forEach((item) => {
        switch (item.format_section) {
          case "Text":
            example = `${example}${item.initial_text}`;
            break;
          case "Number":
            example = `${example}${String(item.iterator).padStart(
              item.format_mod as number,
              "0"
            )}`;
            break;
          case "Year":
            example = `${example}${moment().format(item.format_mod as string)}`;
            break;
          case "Month":
            example = `${example}${moment().format("MM")}`;
            break;
          case "Day":
            example = `${example}${moment().format("DD")}`;
            break;
          default:
            break;
        }
      });
    }

    return example;
  };

  const handleUpdate = () => {
    //check that number field exists
    if (!isNumberUsed.current) {
      setFieldError(true);
      return;
    }

    //submit update
    let values = [];

    for (const selection of currentFormat) {
      if (selection.format_section === "Text") {
        values.push({
          type: "Text",
          value: selection.initial_text,
        });
      }

      if (selection.format_section === "Number") {
        values.push({
          type: "Number",
          value: [selection.format_mod, selection.iterator, selection.iterator],
        });
      }

      if (selection.format_section === "Year") {
        values.push({
          type: "Year",
          value: selection.format_mod,
        });
      }

      if (selection.format_section === "Month") {
        values.push({
          type: "Month",
          value: "",
        });
      }

      if (selection.format_section === "Day") {
        values.push({
          type: "Day",
          value: "",
        });
      }
    }

    updateCaseNumberFormat(values).then(() => {
      enqueueSnackbar(`Case Number Format Updated.`, {
        variant: "success",
      });
    });

    logActivity("Admin", currentUser.user_id, "Updated Case Number Format");
  };

  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Case Number Format
      </div>
      <div>
        {currentFormat && (
          <>
            <div style={{ textAlign: "left", marginBottom: "10px" }}>
              <div>
                Use the options below to create a custom case number that will
                be automatically generated when you create a new case. The
                "Iterator" option must be used.
              </div>
            </div>
            <div
              style={{ textAlign: "left", paddingLeft: "5px", width: "95%" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addText"
                  onClick={addText}
                >
                  <i className="fa fa-plus"></i> + Text
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addNumber"
                  onClick={addNumber}
                >
                  <i className="fa fa-plus"></i> + Iterator
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addYear"
                  onClick={addYear}
                >
                  <i className="fa fa-plus"></i> + Year
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addMonth"
                  onClick={addMonth}
                >
                  <i className="fa fa-plus"></i> + Month
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addDay"
                  onClick={addDay}
                >
                  <i className="fa fa-plus"></i> + Day
                </Button>
              </div>
              {fieldError && (
                <div style={{ color: "orangered", margin: "10px 0px" }}>
                  Please add a number iterator field.
                </div>
              )}
              <div style={{ margin: "10px 0px" }}>EXAMPLE: {example}</div>
              <div style={{ margin: "20px 5px 10px 5px", minHeight: "250px" }}>
                {currentFormat.map((item, index) => {
                  switch (item.format_section) {
                    case "Text":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Text: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              defaultValue={item.initial_text}
                              maxLength={100}
                              style={{ width: "200px" }}
                              placeholder="Enter Text..."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = e.currentTarget.value;
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, initial_text: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Number":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Iterator: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.format_mod as string)}
                              style={{ marginRight: "10px", width: "60px" }}
                              min={1}
                              max={5}
                              title="Select number size."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  1,
                                  5
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.iterator as string)}
                              min={0}
                              style={{ width: "130px" }}
                              placeholder="Initial Count Value"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  0
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i ? { ...cf, iterator: value } : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Year":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Year: </div>
                            <SelectBox
                              size="sm"
                              variant="outlined"
                              defaultValue={item.format_mod as string}
                              width="200px"
                              data={[
                                { label: "YY", value: "YY" },
                                { label: "YYYY", value: "YYYY" },
                              ]}
                              placeholder="Select year format..."
                              onChange={(value: string) => {
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Month":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Month: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="MM"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    case "Day":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Day: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="DD"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    default:
                      return <></>;
                  }
                })}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton resetBtn"
                onClick={() => {
                  isNumberUsed.current = false;
                  setCurrentFormat([]);
                  setExample("");
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton defaultBtn"
                onClick={() => {
                  setExample(getExample(savedFormat.current));
                  setCurrentFormat(savedFormat.current ?? []);
                }}
              >
                Default
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                style={buttonStyle}
                className="summaryButton applyBtn"
                onClick={handleUpdate}
              >
                Apply
              </Button>
            </div>
          </>
        )}
        {!currentFormat && (
          <div>
            <Loader />
          </div>
        )}
      </div>
    </>
  );
};

const EvidenceNumberFormat = () => {
  const { currentUser } = useAuth();

  const [example, setExample] = useState("");
  const [currentFormat, setCurrentFormat] = useState<ItemNumberFormat[] | []>(
    []
  );
  const [fieldError, setFieldError] = useState(false);
  const savedFormat = useRef<ItemNumberFormat[] | null>(null);
  const isNumberUsed = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  useEffect(() => {
    getEvidenceNumberFormat().then((result) => {
      isNumberUsed.current = result.find((r: ItemNumberFormat) => {
        return r.format_section === "Number";
      });
      savedFormat.current = result;
      setExample(getExample(result));
      setCurrentFormat(result);
    });
  }, []);

  const addText = () => {
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Text",
      },
    ]);
  };

  const addNumber = () => {
    if (!isNumberUsed.current) {
      isNumberUsed.current = true;
      setFieldError(false);
      setExample(
        getExample([
          ...currentFormat,
          {
            format_section: "Number",
            format_mod: 1,
            iterator: 1,
          },
        ])
      );
      setCurrentFormat([
        ...currentFormat,
        {
          format_section: "Number",
          format_mod: 1,
          iterator: 1,
        },
      ]);
    }
  };

  const addYear = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Year",
          format_mod: "YY",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Year",
        format_mod: "YY",
      },
    ]);
  };

  const addMonth = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Month",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Month",
      },
    ]);
  };

  const addDay = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Day",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Day",
      },
    ]);
  };

  const getExample = (formatData: ItemNumberFormat[] | null) => {
    let example = "";
    if (formatData) {
      formatData.forEach((item) => {
        switch (item.format_section) {
          case "Text":
            example = `${example}${item.initial_text}`;
            break;
          case "Number":
            example = `${example}${String(item.iterator).padStart(
              item.format_mod as number,
              "0"
            )}`;
            break;
          case "Year":
            example = `${example}${moment().format(item.format_mod as string)}`;
            break;
          case "Month":
            example = `${example}${moment().format("MM")}`;
            break;
          case "Day":
            example = `${example}${moment().format("DD")}`;
            break;
          default:
            break;
        }
      });
    }

    return example;
  };

  const handleUpdate = () => {
    //check that number field exists
    if (!isNumberUsed.current) {
      setFieldError(true);
      return;
    }

    //submit update
    let values = [];

    for (const selection of currentFormat) {
      if (selection.format_section === "Text") {
        values.push({
          type: "Text",
          value: selection.initial_text,
        });
      }

      if (selection.format_section === "Number") {
        values.push({
          type: "Number",
          value: [selection.format_mod, selection.iterator, selection.iterator],
        });
      }

      if (selection.format_section === "Year") {
        values.push({
          type: "Year",
          value: selection.format_mod,
        });
      }

      if (selection.format_section === "Month") {
        values.push({
          type: "Month",
          value: "",
        });
      }

      if (selection.format_section === "Day") {
        values.push({
          type: "Day",
          value: "",
        });
      }
    }

    updateEvidenceNumberFormat(values).then(() => {
      enqueueSnackbar(`Evidence Number Format Updated.`, {
        variant: "success",
      });
    });

    logActivity("Admin", currentUser.user_id, "Updated Evidence Number Format");
  };

  const settingsToShow = [
    {
      setting: "evidence:prevent-duplicates",
      permission: MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE,
    },
  ];

  const handleSettingChange = async (
    setting: SettingEntity,
    value: boolean
  ) => {
    await TenantSettingsAPI.updateTenantSettings({
      id: setting.id,
      enabled: value,
    });
    queryClient.invalidateQueries({ queryKey: ["tenant-settings"] });
  };

  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Evidence Number Format
      </div>
      <div>
        {currentFormat && (
          <>
            <div style={{ textAlign: "left", marginBottom: "10px" }}>
              <div>
                Use the options below to create a custom evidence number that
                will be automatically generated when you create a new evidence.
                The "Iterator" option must be used.
              </div>
            </div>
            <div
              style={{ textAlign: "left", paddingLeft: "5px", width: "95%" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addText"
                  onClick={addText}
                >
                  <i className="fa fa-plus"></i> + Text
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addNumber"
                  onClick={addNumber}
                >
                  <i className="fa fa-plus"></i> + Iterator
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addYear"
                  onClick={addYear}
                >
                  <i className="fa fa-plus"></i> + Year
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addMonth"
                  onClick={addMonth}
                >
                  <i className="fa fa-plus"></i> + Month
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addDay"
                  onClick={addDay}
                >
                  <i className="fa fa-plus"></i> + Day
                </Button>
              </div>
              {fieldError && (
                <div style={{ color: "orangered", margin: "10px 0px" }}>
                  Please add a number iterator field.
                </div>
              )}
              <div style={{ margin: "10px 0px" }}>EXAMPLE: {example}</div>
              <div style={{ margin: "20px 5px 10px 5px" }}>
                {currentFormat.map((item, index) => {
                  switch (item.format_section) {
                    case "Text":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Text: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              defaultValue={item.initial_text}
                              style={{ width: "200px" }}
                              placeholder="Enter Text..."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = e.currentTarget.value;
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, initial_text: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Number":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Iterator: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.format_mod as string)}
                              style={{ marginRight: "10px", width: "60px" }}
                              min={1}
                              max={5}
                              title="Select number size."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  1,
                                  5
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.iterator as string)}
                              min={0}
                              style={{ width: "130px" }}
                              placeholder="Initial Count Value"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  0
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i ? { ...cf, iterator: value } : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Year":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Year: </div>
                            <SelectBox
                              size="sm"
                              variant="outlined"
                              defaultValue={item.format_mod as string}
                              width="200px"
                              data={[
                                { label: "YY", value: "YY" },
                                { label: "YYYY", value: "YYYY" },
                              ]}
                              placeholder="Select year format..."
                              onChange={(value: string) => {
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Month":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Month: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="MM"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    case "Day":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Day: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="DD"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    default:
                      return <></>;
                  }
                })}
              </div>
            </div>
            <div style={{ margin: "60px 0" }}>
              Prevent Duplicate Evidence Numbers
              <SettingSwitchGroup
                handleOptionChange={handleSettingChange}
                settings={settingsToShow}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton resetBtn"
                onClick={() => {
                  isNumberUsed.current = false;
                  setCurrentFormat([]);
                  setExample("");
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton defaultBtn"
                onClick={() => {
                  setExample(getExample(savedFormat.current));
                  setCurrentFormat(savedFormat.current ?? []);
                }}
              >
                Default
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                style={buttonStyle}
                className="summaryButton applyBtn"
                onClick={handleUpdate}
              >
                Apply
              </Button>
            </div>
          </>
        )}
        {!currentFormat && (
          <div>
            <Loader />
          </div>
        )}
      </div>
    </>
  );
};

const StorageNumberFormat = () => {
  const { currentUser } = useAuth();
  const [example, setExample] = useState("");
  const [currentFormat, setCurrentFormat] = useState<ItemNumberFormat[] | []>(
    []
  );
  const [fieldError, setFieldError] = useState(false);
  const savedFormat = useRef<ItemNumberFormat[] | null>(null);
  const isNumberUsed = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getStorageNumberFormat().then((result) => {
      isNumberUsed.current = result.find((r: ItemNumberFormat) => {
        return r.format_section === "Number";
      });
      savedFormat.current = result;
      setExample(getExample(result));
      setCurrentFormat(result);
    });
  }, []);

  const addText = () => {
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Text",
      },
    ]);
  };

  const addNumber = () => {
    if (!isNumberUsed.current) {
      isNumberUsed.current = true;
      setFieldError(false);
      setExample(
        getExample([
          ...currentFormat,
          {
            format_section: "Number",
            format_mod: 1,
            iterator: 1,
          },
        ])
      );
      setCurrentFormat([
        ...currentFormat,
        {
          format_section: "Number",
          format_mod: 1,
          iterator: 1,
        },
      ]);
    }
  };

  const addYear = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Year",
          format_mod: "YY",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Year",
        format_mod: "YY",
      },
    ]);
  };

  const addMonth = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Month",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Month",
      },
    ]);
  };

  const addDay = () => {
    setExample(
      getExample([
        ...currentFormat,
        {
          format_section: "Day",
        },
      ])
    );
    setCurrentFormat([
      ...currentFormat,
      {
        format_section: "Day",
      },
    ]);
  };

  const getExample = (formatData: ItemNumberFormat[] | null) => {
    let example = "";
    if (formatData) {
      formatData.forEach((item) => {
        switch (item.format_section) {
          case "Text":
            example = `${example}${item.initial_text}`;
            break;
          case "Number":
            example = `${example}${String(item.iterator).padStart(
              item.format_mod as number,
              "0"
            )}`;
            break;
          case "Year":
            example = `${example}${moment().format(item.format_mod as string)}`;
            break;
          case "Month":
            example = `${example}${moment().format("MM")}`;
            break;
          case "Day":
            example = `${example}${moment().format("DD")}`;
            break;
          default:
            break;
        }
      });
    }

    return example;
  };

  const handleUpdate = () => {
    //check that number field exists
    if (!isNumberUsed.current) {
      setFieldError(true);
      return;
    }

    //submit update
    let values = [];

    for (const selection of currentFormat) {
      if (selection.format_section === "Text") {
        values.push({
          type: "Text",
          value: selection.initial_text,
        });
      }

      if (selection.format_section === "Number") {
        values.push({
          type: "Number",
          value: [selection.format_mod, selection.iterator, selection.iterator],
        });
      }

      if (selection.format_section === "Year") {
        values.push({
          type: "Year",
          value: selection.format_mod,
        });
      }

      if (selection.format_section === "Month") {
        values.push({
          type: "Month",
          value: "",
        });
      }

      if (selection.format_section === "Day") {
        values.push({
          type: "Day",
          value: "",
        });
      }
    }

    updateStorageNumberFormat(values).then(() => {
      enqueueSnackbar(`Storage Number Format Updated.`, {
        variant: "success",
      });
    });

    logActivity("Admin", currentUser.user_id, "Updated Storage Number Format");
  };

  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>
        Storage Number Format
      </div>
      <div>
        {currentFormat && (
          <>
            <div style={{ textAlign: "left", marginBottom: "10px" }}>
              <div>
                Use the options below to create a custom storage number that
                will be automatically generated when you create a new storage.
                The "Iterator" option must be used.
              </div>
            </div>
            <div
              style={{ textAlign: "left", paddingLeft: "5px", width: "95%" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addText"
                  onClick={addText}
                >
                  <i className="fa fa-plus"></i> + Text
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addNumber"
                  onClick={addNumber}
                >
                  <i className="fa fa-plus"></i> + Iterator
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addYear"
                  onClick={addYear}
                >
                  <i className="fa fa-plus"></i> + Year
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addMonth"
                  onClick={addMonth}
                >
                  <i className="fa fa-plus"></i> + Month
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addDay"
                  onClick={addDay}
                >
                  <i className="fa fa-plus"></i> + Day
                </Button>
              </div>
              {fieldError && (
                <div style={{ color: "orangered", margin: "10px 0px" }}>
                  Please add a number iterator field.
                </div>
              )}
              <div style={{ margin: "10px 0px" }}>EXAMPLE: {example}</div>
              <div style={{ margin: "20px 5px 10px 5px", minHeight: "250px" }}>
                {currentFormat.map((item, index) => {
                  switch (item.format_section) {
                    case "Text":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Text: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              defaultValue={item.initial_text}
                              style={{ width: "200px" }}
                              placeholder="Enter Text..."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = e.currentTarget.value;
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, initial_text: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Number":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Iterator: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.format_mod as string)}
                              style={{ marginRight: "10px", width: "60px" }}
                              min={1}
                              max={5}
                              title="Select number size."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  1,
                                  5
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.iterator as string)}
                              min={0}
                              style={{ width: "130px" }}
                              placeholder="Initial Count Value"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  0
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i ? { ...cf, iterator: value } : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Year":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Year: </div>
                            <SelectBox
                              size="sm"
                              variant="outlined"
                              defaultValue={item.format_mod as string}
                              width="200px"
                              data={[
                                { label: "YY", value: "YY" },
                                { label: "YYYY", value: "YYYY" },
                              ]}
                              placeholder="Select year format..."
                              onChange={(value: string) => {
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Month":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Month: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="MM"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    case "Day":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Day: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="DD"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    default:
                      return <></>;
                  }
                })}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton resetBtn"
                onClick={() => {
                  isNumberUsed.current = false;
                  setCurrentFormat([]);
                  setExample("");
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton defaultBtn"
                onClick={() => {
                  setExample(getExample(savedFormat.current));
                  setCurrentFormat(savedFormat.current ?? []);
                }}
              >
                Default
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                style={buttonStyle}
                className="summaryButton applyBtn"
                onClick={handleUpdate}
              >
                Apply
              </Button>
            </div>
          </>
        )}
        {!currentFormat && (
          <div>
            <Loader />
          </div>
        )}
      </div>
    </>
  );
};

interface NumberFormatSectionProps {
  title: string;
  description: string;
  getFormat: () => Promise<ItemNumberFormat[]>;
  updateFormat: (
    values: AssetNumberFormatSection[]
  ) => Promise<{ success?: boolean } | undefined>;
  logLabel: string;
}

// Generic builder shared by the gated asset section. The three sections above
// are intentionally left as they were (ENG-2250 adds assets only).
const NumberFormatSection = ({
  title,
  description,
  getFormat,
  updateFormat,
  logLabel,
}: NumberFormatSectionProps) => {
  const { currentUser } = useAuth();
  const [example, setExample] = useState("");
  const [currentFormat, setCurrentFormat] = useState<ItemNumberFormat[] | null>(
    null
  );
  const [fieldError, setFieldError] = useState(false);
  const savedFormat = useRef<ItemNumberFormat[] | null>(null);
  const isNumberUsed = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getFormat().then((result) => {
      // A failed GET resolves { success: false } (already toasted); keep the
      // loader up rather than offering an empty builder over the real format.
      if (!Array.isArray(result)) return;
      isNumberUsed.current = result.some((r) => r.format_section === "Number");
      savedFormat.current = result;
      setExample(getExample(result));
      setCurrentFormat(result);
    });
  }, [getFormat]);

  const addText = () => {
    setCurrentFormat([
      ...(currentFormat ?? []),
      {
        format_section: "Text",
      },
    ]);
  };

  const addNumber = () => {
    if (!isNumberUsed.current) {
      isNumberUsed.current = true;
      setFieldError(false);
      setExample(
        getExample([
          ...(currentFormat ?? []),
          {
            format_section: "Number",
            format_mod: 1,
            iterator: 1,
          },
        ])
      );
      setCurrentFormat([
        ...(currentFormat ?? []),
        {
          format_section: "Number",
          format_mod: 1,
          iterator: 1,
        },
      ]);
    }
  };

  const addYear = () => {
    setExample(
      getExample([
        ...(currentFormat ?? []),
        {
          format_section: "Year",
          format_mod: "YY",
        },
      ])
    );
    setCurrentFormat([
      ...(currentFormat ?? []),
      {
        format_section: "Year",
        format_mod: "YY",
      },
    ]);
  };

  const addMonth = () => {
    setExample(
      getExample([
        ...(currentFormat ?? []),
        {
          format_section: "Month",
        },
      ])
    );
    setCurrentFormat([
      ...(currentFormat ?? []),
      {
        format_section: "Month",
      },
    ]);
  };

  const addDay = () => {
    setExample(
      getExample([
        ...(currentFormat ?? []),
        {
          format_section: "Day",
        },
      ])
    );
    setCurrentFormat([
      ...(currentFormat ?? []),
      {
        format_section: "Day",
      },
    ]);
  };

  const getExample = (formatData: ItemNumberFormat[] | null) => {
    let example = "";
    if (formatData) {
      formatData.forEach((item) => {
        switch (item.format_section) {
          case "Text":
            example = `${example}${item.initial_text ?? ""}`;
            break;
          case "Number":
            example = `${example}${String(item.iterator).padStart(
              item.format_mod as number,
              "0"
            )}`;
            break;
          case "Year":
            example = `${example}${moment().format(item.format_mod as string)}`;
            break;
          case "Month":
            example = `${example}${moment().format("MM")}`;
            break;
          case "Day":
            example = `${example}${moment().format("DD")}`;
            break;
          default:
            break;
        }
      });
    }

    return example;
  };

  const handleUpdate = () => {
    //check that number field exists
    if (!isNumberUsed.current) {
      setFieldError(true);
      return;
    }

    //submit update
    let values = [];

    for (const selection of currentFormat ?? []) {
      if (selection.format_section === "Text") {
        values.push({
          type: "Text",
          value: selection.initial_text ?? "",
        });
      }

      if (selection.format_section === "Number") {
        values.push({
          type: "Number",
          value: [selection.format_mod, selection.iterator, selection.iterator],
        });
      }

      if (selection.format_section === "Year") {
        values.push({
          type: "Year",
          value: selection.format_mod,
        });
      }

      if (selection.format_section === "Month") {
        values.push({
          type: "Month",
          value: "",
        });
      }

      if (selection.format_section === "Day") {
        values.push({
          type: "Day",
          value: "",
        });
      }
    }

    updateFormat(values).then((result) => {
      // The api layer resolves { success: false } on an HTTP error instead of
      // rejecting, so a failed PATCH must not toast success or log an update.
      if (result?.success === false) return;
      // Default restores savedFormat, so it must track the last applied format.
      savedFormat.current = currentFormat;
      enqueueSnackbar(`${title} Updated.`, {
        variant: "success",
      });
      logActivity("Admin", currentUser.user_id, logLabel);
    });
  };

  return (
    <>
      <div style={{ fontSize: "large", marginBottom: "20px" }}>{title}</div>
      <div>
        {currentFormat && (
          <>
            <div style={{ textAlign: "left", marginBottom: "10px" }}>
              <div>{description}</div>
            </div>
            <div
              style={{ textAlign: "left", paddingLeft: "5px", width: "95%" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addText"
                  onClick={addText}
                >
                  <i className="fa fa-plus"></i> + Text
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addNumber"
                  onClick={addNumber}
                >
                  <i className="fa fa-plus"></i> + Iterator
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addYear"
                  onClick={addYear}
                >
                  <i className="fa fa-plus"></i> + Year
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addMonth"
                  onClick={addMonth}
                >
                  <i className="fa fa-plus"></i> + Month
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  style={buttonStyle}
                  className="summaryButton addDay"
                  onClick={addDay}
                >
                  <i className="fa fa-plus"></i> + Day
                </Button>
              </div>
              {fieldError && (
                <div style={{ color: "orangered", margin: "10px 0px" }}>
                  Please add a number iterator field.
                </div>
              )}
              <div style={{ margin: "10px 0px" }}>EXAMPLE: {example}</div>
              <div style={{ margin: "20px 5px 10px 5px", minHeight: "250px" }}>
                {currentFormat.map((item, index) => {
                  switch (item.format_section) {
                    case "Text":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Text: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              defaultValue={item.initial_text}
                              style={{ width: "200px" }}
                              placeholder="Enter Text..."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = e.currentTarget.value;
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, initial_text: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Number":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Iterator: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.format_mod as string)}
                              style={{ marginRight: "10px", width: "60px" }}
                              min={1}
                              max={5}
                              title="Select number size."
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  1,
                                  5
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                            <TextInput
                              size="sm"
                              variant="outlined"
                              type="number"
                              defaultValue={parseInt(item.iterator as string)}
                              min={0}
                              style={{ width: "130px" }}
                              placeholder="Initial Count Value"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const value = clampNumber(
                                  e.currentTarget.value,
                                  0
                                );
                                const temp = currentFormat.map((cf, i) =>
                                  index === i ? { ...cf, iterator: value } : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Year":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Year: </div>
                            <SelectBox
                              size="sm"
                              variant="outlined"
                              defaultValue={item.format_mod as string}
                              width="200px"
                              data={[
                                { label: "YY", value: "YY" },
                                { label: "YYYY", value: "YYYY" },
                              ]}
                              placeholder="Select year format..."
                              onChange={(value: string) => {
                                const temp = currentFormat.map((cf, i) =>
                                  index === i
                                    ? { ...cf, format_mod: value }
                                    : cf
                                );

                                setExample(getExample(temp));
                                setCurrentFormat(temp);
                              }}
                            />
                          </div>
                        </div>
                      );
                    case "Month":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Month: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="MM"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    case "Day":
                      return (
                        <div key={index}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "10px",
                            }}
                          >
                            <div style={{ minWidth: "100px" }}>Day: </div>
                            <TextInput
                              size="sm"
                              variant="outlined"
                              maxLength={100}
                              style={{ width: "200px" }}
                              defaultValue="DD"
                              disabled={true}
                            />
                          </div>
                        </div>
                      );
                    default:
                      return <></>;
                  }
                })}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton resetBtn"
                onClick={() => {
                  isNumberUsed.current = false;
                  setCurrentFormat([]);
                  setExample("");
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                style={buttonStyle}
                className="summaryButton defaultBtn"
                onClick={() => {
                  isNumberUsed.current = (savedFormat.current ?? []).some(
                    (r) => r.format_section === "Number"
                  );
                  setExample(getExample(savedFormat.current));
                  setCurrentFormat(savedFormat.current ?? []);
                }}
              >
                Default
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                style={buttonStyle}
                className="summaryButton applyBtn"
                onClick={handleUpdate}
              >
                Apply
              </Button>
            </div>
          </>
        )}
        {!currentFormat && (
          <div>
            <Loader />
          </div>
        )}
      </div>
    </>
  );
};

const AssetNumberFormat = () => (
  <NumberFormatSection
    title="Asset Number Format"
    description={
      'Use the options below to create a custom asset number that will be automatically generated when you create a new asset. The "Iterator" option must be used.'
    }
    getFormat={getAssetNumberFormat}
    updateFormat={updateAssetNumberFormat}
    logLabel="Updated Asset Number Format"
  />
);

const ItemNumberFormatSettings = styled(({ className }) => {
  const { hasFeatures, FEATURES } = useFeatures();
  const showAssets = hasFeatures([FEATURES.LAB_ASSETS]);

  return (
    <div className={className} style={{ maxWidth: 750, padding: "0px 30px" }}>
      <CaseNumberFormat />
      <Divider style={{ margin: "20px 0px" }} />
      <EvidenceNumberFormat />
      <Divider style={{ margin: "20px 0px" }} />
      <StorageNumberFormat />
      <Divider style={{ margin: "20px 0px" }} />
      {showAssets && (
        <>
          <AssetNumberFormat />
          <Divider style={{ margin: "20px 0px" }} />
        </>
      )}
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

export default ItemNumberFormatSettings;
